import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, message, Tag, Card, Typography, Empty, Space, Select, Col, Input, Form, Modal, Row } from 'antd';
import { ReloadOutlined, ExportOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import FilterPanel from '../../components/common/FilterPanel';
import { itemStoreRelationshipService } from '../../services/itemStoreRelationshipService';
import { itemService } from '../../services/itemService';
import { warehouseService } from '../../services/warehouseService';
import dayjs from 'dayjs';
import {
  ItemStoreRelationship,
  ItemStoreRelationshipFilters,
  ItemStoreRelationshipPaginationParams,
  ItemStoreRelationshipSortField,
  SortOrder,
  Item,
  Store,
  ITEM_STORE_RELATIONSHIP_STATUS_OPTIONS,
  TO_BE_DETAIL_OPTIONS
} from '../../types/item-store-relationship';

const { Title } = Typography;
const { Option } = Select;
const { confirm } = Modal;

const ItemStoreRelationshipList: React.FC = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  
  // State management
  const [loading, setLoading] = useState<boolean>(false);
  const [storeOptionsLoading, setStoreOptionsLoading] = useState<boolean>(false);
  const [itemOptionsLoading, setItemOptionsLoading] = useState<boolean>(false);
  const [relationships, setRelationships] = useState<ItemStoreRelationship[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [current, setCurrent] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [filters, setFilters] = useState<ItemStoreRelationshipFilters>({});
  const [sortField, setSortField] = useState<ItemStoreRelationshipSortField>();
  const [sortOrder, setSortOrder] = useState<'ascend' | 'descend'>('descend');
  
  // Options for dropdowns
  const [storeOptions, setStoreOptions] = useState<Store[]>([]);
  const [itemOptions, setItemOptions] = useState<Item[]>([]);

  // 批量操作相关状态
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [isBatchModalVisible, setIsBatchModalVisible] = useState(false);
  const [newDisplayType, setNewDisplayType] = useState<string>('');

  // Fetch relationships data
  const fetchRelationships = async () => {
    setLoading(true);
    try {
      const response = await itemStoreRelationshipService.getItemStoreRelationships(
        filters,
        {
          currentPage: current,
          pageSize,
          sortBy: sortField,
          sortOrder: sortOrder === 'ascend' ? 'asc' : 'desc'
        }
      );

      if (response.success && response.data) {
        setRelationships(response.data.tableData);
        setTotal(response.data.paging.totalCount);
      } else {
        // console.error(t('itemStoreRelationship.list.messages.dataFormatError'), response);
        setRelationships([]);
        setTotal(0);
        if (response.errorMessage) {
          message.error(response.errorMessage);
        }
      }
    } catch (error) {
      message.error(error.message || t('itemStoreRelationship.list.messages.fetchFailed'));
      console.error(t('itemStoreRelationship.list.messages.fetchFailed'), error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch store options
  const fetchStoreOptions = async (searchValue: string = '') => {
    setStoreOptionsLoading(true);
    try {
      const response = await warehouseService.listWarehouse({searchStr: searchValue});
      if (response.success && response.data) {
        setStoreOptions(response.data);
      }
    } catch (error) {
      console.error(t('itemStoreRelationship.list.messages.fetchStoreOptionsFailed'), error);
    } finally {
      setStoreOptionsLoading(false);
    }
  };

  // Fetch item options
  const fetchItemOptions = async (searchValue: string = '') => {
    setItemOptionsLoading(true);
    try {
      let req = {
        searchStr:searchValue,
        pageSize: 100,
        currentPage: 1
      }
      const response = await itemService.listItems(req);
      if (response.success && response.data) {
        setItemOptions(response.data);
      }
    } catch (error) {
      console.error(t('itemStoreRelationship.list.messages.fetchItemOptionsFailed'), error);
    } finally {
      setItemOptionsLoading(false);
    }
  };

  useEffect(() => {
    fetchRelationships();
  }, [current, pageSize, filters, sortField, sortOrder]);

  useEffect(() => {
    fetchStoreOptions();
    fetchItemOptions();
  }, []);

  const handleTableChange = (
    pagination: any,
    _filters: any,
    sorter: any
  ) => {
    setCurrent(pagination.current);
    setPageSize(pagination.pageSize);

    if (sorter.field && sorter.order) {
      setSortField(sorter.field);
      setSortOrder(sorter.order);
    }
  };

  const handleSearch = (values: any) => {
    setFilters({ ...filters, ...values });
    setCurrent(1); // 重置到第一页
  };

  const handleReset = () => {
    form.resetFields();
    setFilters({});
    setCurrent(1);
  };

  // Format datetime display
  const formatDateTime = (timestamp: number): string => {
    return dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss');
  };

  // Render status tag
  const renderStatusTag = (status: string) => {
    const statusOption = ITEM_STORE_RELATIONSHIP_STATUS_OPTIONS.find(option => option.value === status);
    return (
      <Tag color={statusOption?.color || 'default'}>
        {statusOption ? t(statusOption.label) : status}
      </Tag>
    );
  };

  // Render display type tag
  const renderDisplayTypeTag = (displayType: string) => {
    let color = 'default';
    if (displayType === 'PDQ') {
      color = 'blue';
    } else if (displayType === 'MU') {
      color = 'green';
    }
    return <Tag color={color}>{displayType}</Tag>;
  };

  // Render to be detail tag
  const renderToBeDetailTag = (toBeDetail) => {
    const option = TO_BE_DETAIL_OPTIONS.find(opt => opt.value === toBeDetail);
    return (
      <Tag color={option?.color || 'default'}>
        {option ? t(option.label) : ''}
      </Tag>
    );
  };

  // 批量操作相关函数
  const handleBatchUpdateDisplayType = () => {
    if (selectedRowKeys.length === 0) {
      message.warning(t('itemStoreRelationship.list.messages.selectItemsFirst'));
      return;
    }
    setIsBatchModalVisible(true);
  };

  const handleBatchModalOk = async () => {
    if (!newDisplayType) {
      message.warning(t('itemStoreRelationship.list.messages.selectDisplayType'));
      return;
    }

    try {
      setLoading(true);
      // 调用实际的API来更新displayType
      const idList = selectedRowKeys.map(key => Number(key));
      const response = await itemStoreRelationshipService.batchUpdateDisplayType(idList, newDisplayType);
      
      if (response.success) {
        // 更新本地数据
        const updatedRelationships = relationships.map(item => {
          if (selectedRowKeys.includes(item.id)) {
            return { ...item, displayType: newDisplayType };
          }
          return item;
        });
        
        setRelationships(updatedRelationships);
        setSelectedRowKeys([]);
        setIsBatchModalVisible(false);
        setNewDisplayType('');
        message.success(t('itemStoreRelationship.list.messages.updateDisplayTypeSuccess'));
      } else {
        message.error(response.errorMessage || t('itemStoreRelationship.list.messages.updateDisplayTypeFailed'));
      }
    } catch (error) {
      message.error(error.message || t('itemStoreRelationship.list.messages.updateDisplayTypeFailed'));
      console.error(t('itemStoreRelationship.list.messages.updateDisplayTypeFailed'), error);
    } finally {
      setLoading(false);
    }
  };

  const handleBatchModalCancel = () => {
    setIsBatchModalVisible(false);
    setNewDisplayType('');
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys: React.Key[]) => {
      setSelectedRowKeys(selectedKeys);
    },
  };

  const columns: ColumnsType<ItemStoreRelationship> = [
    {
      title: t('itemStoreRelationship.list.columns.storeCode'),
      dataIndex: 'storeCode',
      key: 'storeCode',
      // sorter: true,
      // sortOrder: sortField === 'storeCode' ? sortOrder : null,
      width: 120,
    },
    {
      title: t('itemStoreRelationship.list.columns.itemCode'),
      dataIndex: 'itemCode',
      key: 'itemCode',
      // sorter: true,
      // sortOrder: sortField === 'itemCode' ? sortOrder : null,
      width: 120,
    },
     {
      title: t('itemStoreRelationship.list.columns.itemName'),
      dataIndex: 'itemName',
      key: 'itemName',
      // sorter: true,
      // sortOrder: sortField === 'itemCode' ? sortOrder : null,
      width: 150,
    },
    {
      title: t('itemStoreRelationship.list.columns.divisionName'),
      dataIndex: 'divisionName',
      key: 'divisionName',
      // sorter: true,
      // sortOrder: sortField === 'itemCode' ? sortOrder : null,
      width: 150,
    },
    {
      title: t('itemStoreRelationship.list.columns.className'),
      dataIndex: 'className',
      key: 'className',
      // sorter: true,
      // sortOrder: sortField === 'itemCode' ? sortOrder : null,
      width: 150,
    },
    {
      title: t('itemStoreRelationship.list.columns.subClassName'),
      dataIndex: 'subClassName',
      key: 'subClassName',
      // sorter: true,
      // sortOrder: sortField === 'itemCode' ? sortOrder : null,
      width: 150,
    },
    {
      title: t('itemStoreRelationship.list.columns.ownerCode'),
      dataIndex: 'ownerCode',
      key: 'ownerCode',
      // sorter: true,
      // sortOrder: sortField === 'ownerCode' ? sortOrder : null,
      width: 120,
    },
    {
      title: t('itemStoreRelationship.list.columns.displayType'),
      dataIndex: 'displayType',
      key: 'displayType',
      width: 120,
      render: (displayType: string) => renderDisplayTypeTag(displayType),
    },
    {
      title: t('itemStoreRelationship.list.columns.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => renderStatusTag(status),
      width: 100,
    },
    {
      title: t('itemStoreRelationship.list.columns.toBeDetail'),
      dataIndex: 'toBeDetail',
      key: 'toBeDetail',
      render: (toBeDetail: boolean) => renderToBeDetailTag(toBeDetail),
      width: 100,
    },
    {
      title: t('itemStoreRelationship.list.columns.createTime'),
      dataIndex: 'gmtCreate',
      key: 'gmtCreate',
      // sorter: true,
      // sortOrder: sortField === 'gmtCreate' ? sortOrder : null,
      render: (timestamp: number) => formatDateTime(timestamp),
      width: 180,
    },
    {
      title: t('itemStoreRelationship.list.columns.modifyTime'),
      dataIndex: 'gmtModified',
      key: 'gmtModified',
      // sorter: true,
      // sortOrder: sortField === 'gmtModified' ? sortOrder : null,
      render: (timestamp: number) => formatDateTime(timestamp),
      width: 180,
    },
  ];

  return (
    <div>
      {/* 筛选面板 */}
        <FilterPanel
          form={form}
          onFilter={handleSearch}
          onReset={handleReset}
          title={t('menu.itemStoreRelationship')}
          className="filter-card"
        >
          <Col span={6}>
            <Form.Item 
              name="storeCode" 
              label={t('itemStoreRelationship.list.filter.storeCode')}
            >
              <Select
                placeholder={t('itemStoreRelationship.list.filter.storePlaceholder')}
                allowClear
                showSearch
                loading={storeOptionsLoading}
                onSearch={fetchStoreOptions}
                filterOption={false}
                style={{ width: '100%' }}
              >
                {storeOptions.map(store => (
                  <Option key={store.code} value={store.code}>
                    {store.code} - {store.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item 
              name="itemCode" 
              label={t('itemStoreRelationship.list.filter.itemCode')}
            >
              <Select
                placeholder={t('itemStoreRelationship.list.filter.itemPlaceholder')}
                allowClear
                showSearch
                loading={itemOptionsLoading}
                onSearch={fetchItemOptions}
                filterOption={false}
                style={{ width: '100%' }}
              >
                {itemOptions.map(item => (
                  <Option key={item.code} value={item.code}>
                    {item.code} - {item.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item 
              name="status" 
              label={t('itemStoreRelationship.list.filter.status')}
            >
              <Select placeholder={t('itemStoreRelationship.list.filter.statusPlaceholder')} allowClear style={{ width: '100%' }}>
                {ITEM_STORE_RELATIONSHIP_STATUS_OPTIONS.map(option => (
                  <Option key={option.value} value={option.value}>
                    {t(option.label)}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item 
              name="toBeDetail" 
              label={t('itemStoreRelationship.list.filter.toBeDetail')}
            >
              <Select placeholder={t('itemStoreRelationship.list.filter.toBeDetailPlaceholder')} allowClear style={{ width: '100%' }}>
                {TO_BE_DETAIL_OPTIONS.map(option => (
                  <Option key={option.value} value={option.value}>
                    {t(option.label)}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </FilterPanel>
      <Card>
        {/* 批量操作按钮 */}
        <div style={{ marginBottom: 16 }}>
          <Button 
            type="primary" 
            onClick={handleBatchUpdateDisplayType}
            disabled={selectedRowKeys.length === 0}
          >
            {t('itemStoreRelationship.list.batchUpdateDisplayType')}
          </Button>
          <span style={{ marginLeft: 8 }}>
            {selectedRowKeys.length > 0 ? t('itemStoreRelationship.list.selectedItemsCount', { count: selectedRowKeys.length }) : ''}
          </span>
        </div>

        {/* 数据表格 */}
        <Table
          columns={columns}
          dataSource={relationships}
          rowKey="id"
          loading={loading}
          rowSelection={rowSelection}
          pagination={{
            current,
            pageSize,
            total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => t('itemStoreRelationship.list.pagination.displayRange', { total, start: range[0], end: range[1] }),
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          onChange={handleTableChange}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={t('common.noData')}
              />
            ),
          }}
          scroll={{ x: 1400, y: 500 }}
        />
      </Card>

      {/* 批量更新displayType模态框 */}
      <Modal
        title={t('itemStoreRelationship.list.batchUpdateDisplayTypeModalTitle')}
        open={isBatchModalVisible}
        onOk={handleBatchModalOk}
        onCancel={handleBatchModalCancel}
        okText={t('common.confirm')}
        cancelText={t('common.cancel')}
      >
        <Form layout="vertical">
          <Form.Item 
            label={t('itemStoreRelationship.list.columns.displayType')}
            required
          >
            <Select
              value={newDisplayType}
              onChange={setNewDisplayType}
              placeholder={t('itemStoreRelationship.list.selectDisplayType')}
            >
              <Option value="PDQ">{t('itemStoreRelationship.list.displayTypeOptions.pdq')}</Option>
              <Option value="MU">{t('itemStoreRelationship.list.displayTypeOptions.mu')}</Option>
            </Select>
          </Form.Item>
          <Row>
            <Col span={24}>
              <p>{t('itemStoreRelationship.list.selectedItemsToUpdate', { count: selectedRowKeys.length })}</p>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default ItemStoreRelationshipList;